/**
 * SubmodelConfigPanel Component for UI-Model-1
 *
 * Config panel for editing submodel properties when a submodel header is clicked.
 * Ported from UI-Model's SubmodelConfig.tsx, adapted for UI-Model-1's API-direct architecture.
 *
 * Sections:
 * 1. Submodel Name (read-only)
 * 2. Show Title (checkbox)
 * 3. Display Name (conditional on Show Title, debounced)
 * 4. Hide when no products available (checkbox with info tooltip)
 * 5. Exclusions (radio group: Hide / Disable)
 */

import React, { useCallback, useMemo } from 'react';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { useDebouncedInput } from '../../hooks/useDebouncedInput';
import { isSubmodelFull, SubmodelConfigurationAPI } from '../../types/api.types';
import { ContainerAPI } from '../../types/state.types';
import { SubModelIcon } from 'assests/GmsIcons';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Radio from 'Components/DesignSystem/Radio/Radio';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';

interface SubmodelConfigPanelProps {
  submodelId: string;
  containerId: string;
}

export const SubmodelConfigPanel = ({
  submodelId,
  containerId,
}: SubmodelConfigPanelProps) => {
  const { state, dispatch, getContainer } = useUIModelState();
  const { activePageId } = state.editorState;

  // Find the container and the CLICKED submodel — a free-style mixed container
  // holds several bindings in content.submodels; resolve by the selected id
  // (falling back to the singular binding, the only one restricted models have).
  const { element: container } = getContainer(containerId);
  const submodel =
    container?.content?.submodels?.find((s) => s.id === submodelId) ??
    container?.content?.submodel;
  const isFullSubmodel = isSubmodelFull(submodel);

  // Read config values from the submodel
  const config: SubmodelConfigurationAPI = useMemo(() => {
    if (isFullSubmodel) {
      return submodel.configuration;
    }
    return {};
  }, [isFullSubmodel, submodel]);

  const submodelName = isFullSubmodel
    ? config.fieldName || submodel.name || ''
    : '';

  const showTitle = config.showTitle ?? true;
  const hideIfNoProducts = config.hideIfNoProducts ?? false;
  const exclusionType = config.exclusionType ?? 'hide';

  // Find the canvas ID for the active page
  const canvasId = useMemo(() => {
    if (!activePageId || !state.model?.layout.canvas) return null;
    return Object.keys(state.model.layout.canvas).find(
      (key) => state.model!.layout.canvas[key].pageId === activePageId
    ) ?? null;
  }, [activePageId, state.model]);

  // Update submodel configuration on this container AND all nested containers
  // that share the same submodel ID. When a submodel is assigned, its full
  // SubmodelFullAPI is copied to parent + all children. Config changes must
  // propagate the same way or children retain stale values.
  const updateSubmodelConfig = useCallback(
    (updates: Partial<SubmodelConfigurationAPI>) => {
      if (!canvasId || !state.model || !isFullSubmodel) return;

      const canvas = state.model.layout.canvas[canvasId];
      if (!canvas) return;

      const targetSubmodelId = submodel.id;

      // Recursively update every container in the tree whose submodel matches —
      // both the singular binding and any matching entry in the free-style
      // content.submodels list (each binding carries its own configuration).
      const updateTree = (containers: ContainerAPI[]): ContainerAPI[] =>
        containers.map((cont) => {
          let updated = cont;

          const matchesSingular =
            cont.content?.submodel &&
            cont.content.submodel.id === targetSubmodelId &&
            isSubmodelFull(cont.content.submodel);
          const matchesList = cont.content?.submodels?.some(
            (s) => s.id === targetSubmodelId
          );

          if (matchesSingular || matchesList) {
            updated = {
              ...cont,
              content: {
                ...cont.content,
                submodel: matchesSingular && isSubmodelFull(cont.content!.submodel)
                  ? {
                      ...cont.content!.submodel,
                      configuration: {
                        ...cont.content!.submodel.configuration,
                        ...updates,
                      },
                    }
                  : cont.content?.submodel,
                submodels: matchesList
                  ? cont.content!.submodels!.map((s) =>
                      s.id === targetSubmodelId
                        ? { ...s, configuration: { ...s.configuration, ...updates } }
                        : s
                    )
                  : cont.content?.submodels,
              },
            };
          }

          // Recurse into nested children
          if (updated.content?.nestedContainers?.length) {
            const updatedNested = updateTree(updated.content.nestedContainers);
            if (updatedNested !== updated.content.nestedContainers) {
              updated = {
                ...updated,
                content: { ...updated.content, nestedContainers: updatedNested },
              };
            }
          }

          return updated;
        });

      const updatedContainers = updateTree(canvas.containers);

      dispatch({
        type: 'SET_MODEL',
        payload: {
          ...state.model,
          layout: {
            ...state.model.layout,
            canvas: {
              ...state.model.layout.canvas,
              [canvasId]: {
                ...canvas,
                containers: updatedContainers,
              },
            },
          },
        },
      });

      dispatch({
        type: 'MARK_DIRTY',
        payload: { section: 'canvas', canvasId },
      });
    },
    [canvasId, state.model, containerId, dispatch, isFullSubmodel, submodel]
  );

  // Debounced display name input
  const {
    localValue: localDisplayName,
    handleChange: handleDisplayNameChange,
  } = useDebouncedInput(
    config.displayName || '',
    (value) => updateSubmodelConfig({ displayName: value }),
    500
  );

  const handleShowTitleChange = useCallback(
    (checked: boolean) => {
      updateSubmodelConfig({ showTitle: checked });
    },
    [updateSubmodelConfig]
  );

  const handleHideIfNoProductsChange = useCallback(
    (checked: boolean) => {
      updateSubmodelConfig({ hideIfNoProducts: checked });
    },
    [updateSubmodelConfig]
  );

  const handleExclusionTypeChange = useCallback(
    (type: 'hide' | 'disable') => {
      updateSubmodelConfig({ exclusionType: type });
    },
    [updateSubmodelConfig]
  );

  if (!container || !isFullSubmodel) {
    return (
      <div className="tw-text-center tw-py-8">
        <p className="tw-text-ink-secondary tw-text-sm">Submodel not found</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4 tw-text-ink-secondary icon-follow-ink">
        <SubModelIcon />
        <h2 className="tw-text-ink-secondary tw-text-sm tw-font-semibold">
          Submodel
        </h2>
      </div>

      {/* Submodel Name (read-only) */}
      <div className="tw-mb-4">
        <TextField
          label="Submodel Name"
          labelSize="sm"
          value={submodelName}
          disabled
          placeholder="Submodel name"
          data-testid="configpanel-submodelconfigpanel-input-1"
        />
      </div>

      {/* Show Title Checkbox */}
      <div className="tw-mb-4">
        <Checkbox
          label="Show Title"
          checked={showTitle}
          onChange={handleShowTitleChange}
          data-testid="configpanel-submodelconfigpanel-input-2"
        />

        {/* Display Name (conditional on Show Title) */}
        {showTitle && (
          <div className="tw-mt-3 tw-ml-6">
            <TextField
              label="Display Name"
              labelSize="sm"
              value={localDisplayName}
              onChange={handleDisplayNameChange}
              placeholder="Enter display name"
              data-testid="configpanel-submodelconfigpanel-input-3"
            />
          </div>
        )}
      </div>

      {/* Hide when no products */}
      <div className="tw-mb-6 tw-border-b tw-border-edge tw-border-t tw-py-2">
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <Checkbox
            label="Hide, when no products are available"
            checked={hideIfNoProducts}
            onChange={handleHideIfNoProductsChange}
            data-testid="configpanel-submodelconfigpanel-input-4"
          />
          <InfoTooltip
            message="When enabled, automatically hides the sections (Page/SubModel/Category) if no compatible products are available"
            label="About hiding empty sections"
            placement="bottom"
            usePortal
          />
        </div>
      </div>

      {/* Exclusions */}
      <div className="tw-mb-4">
        <h3 className="tw-text-ink-secondary tw-text-sm tw-mb-3">Exclusions</h3>
        <div className="tw-flex tw-items-center tw-space-x-8">
          <Radio
            name="submodel-exclusion-type"
            label="Hide"
            value="hide"
            checked={exclusionType === 'hide'}
            onChange={() => handleExclusionTypeChange('hide')}
            data-testid="configpanel-submodelconfigpanel-input-5"
          />
          <Radio
            name="submodel-exclusion-type"
            label="Disable"
            value="disable"
            checked={exclusionType === 'disable'}
            onChange={() => handleExclusionTypeChange('disable')}
            data-testid="configpanel-submodelconfigpanel-input-6"
          />
        </div>
      </div>
    </div>
  );
};

export default SubmodelConfigPanel;
